import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Sparkles, BookOpen, Palette, Users, Zap, Trophy } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const OnboardingTutorial = ({ user }) => {
  const [show, setShow] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Check if user has seen onboarding
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    
    if (!hasSeenOnboarding && user) {
      // Show onboarding after 1 second delay
      setTimeout(() => setShow(true), 1000);
    }
  }, [user]);

  const steps = [
    {
      icon: Sparkles,
      title: 'Welcome to Trap Comix!',
      description: 'Your platform for discovering and creating underground comics, art, and stories. Let\'s show you around!',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: BookOpen,
      title: 'Discover Amazing Content',
      description: 'Browse thousands of comics, novels, and artwork from underrepresented creators. Use the Feed to see latest drops from creators you follow.',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Users,
      title: 'Follow & Connect',
      description: 'Follow your favorite creators to stay updated. React with Fire 🔥, Diamond 💎, and more to show support. Post on their Fan Walls!',
      gradient: 'from-cyan-500 to-blue-500'
    },
    {
      icon: Zap,
      title: 'Earn Trap Energy',
      description: 'Upload content, engage with fans, and climb the Leaderboard! Artwork (+5), Episodes (+10), Music (+15). Level up your creator tier!',
      gradient: 'from-yellow-500 to-orange-500'
    },
    {
      icon: user?.role === 'artist' || user?.role === 'author' ? Palette : Trophy,
      title: user?.role === 'artist' || user?.role === 'author' ? 'Start Creating' : 'Explore & Enjoy',
      description: user?.role === 'artist' || user?.role === 'author' 
        ? 'Hit the Publish button to upload comics, artwork, or music. You own your IP, you keep your revenue. Your story, your rules.'
        : 'Discover trending content, join communities, and support creators. Your engagement helps them grow!',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  const handleClose = () => {
    localStorage.setItem('hasSeenOnboarding', 'true');
    setShow(false);
  };

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  if (!show) return null;

  const currentStep = steps[step];
  const Icon = currentStep.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <Card className="bg-gray-900 border-gray-800 max-w-lg w-full animate-in fade-in zoom-in duration-300">
        <CardContent className="p-0">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className={`bg-gradient-to-r ${currentStep.gradient} p-8 text-center`}>
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-4">
              <Icon className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{currentStep.title}</h2>
          </div>

          {/* Content */}
          <div className="p-8">
            <p className="text-gray-300 text-center text-lg mb-6">
              {currentStep.description}
            </p>

            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mb-6">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 rounded-full transition-all ${
                    index === step
                      ? 'w-8 bg-gradient-to-r from-orange-500 to-red-500'
                      : index < step
                      ? 'w-2 bg-orange-500/50'
                      : 'w-2 bg-gray-700'
                  }`}
                />
              ))}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between gap-3">
              <Button
                onClick={handlePrevious}
                variant="outline"
                className={`border-gray-700 ${step === 0 ? 'invisible' : ''}`}
              >
                Previous
              </Button>

              <Button
                onClick={handleNext}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 flex items-center gap-2"
              >
                {step === steps.length - 1 ? "Let's Go!" : 'Next'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Skip Option */}
            <button
              onClick={handleClose}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-400 transition-colors mt-4"
            >
              Skip tutorial
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingTutorial;
